import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Dict } from '@se/se.platform.base';
import { UserConfiguration, VNextGridUserPreference } from 'estensa-module';
import { firstValueFrom, Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
    ExecutionWorklistSearchWorklist,
    ISpecialtyWorklistSearchParams,
    IVisitWorklistSearchingParams,
    SearchCda,
    WorklistMpsSearchingParams,
    WorklistSearchWorklist,
} from '../models';
import {
    selectSeSearchItems,
    selectSeSearchSelectedItemIds,
    selectSeSearchSelectedItems,
} from '../root-store/root.selectors';
import { RootState } from '../root-store/root.state';
import { selectSeButtonsToolbarButtons } from '../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.selectors';
import { EstensaCollectionService } from '../services/angularjs/estensa-collection.service';
import { NotificationService } from '../services/notification.service';
import { EcgService } from '../services/rest/ecg.service';
import { MPSService } from '../services/rest/mps.service';
import { VisitService } from '../services/rest/visit.service';
import { WorklistService } from '../services/rest/worklist.service';
import { RadioSettingsService } from '../services/settings/radio-settings.service';
import { UserConfigurationSettingsService } from '../services/settings/user-configuration-settings.service';
// Import for double surname feature
import { PatientSettingsService } from '../services/settings/patient-settings.service';
import { ServerRootService } from '../utils/routing/serverroot.service';
import { orderCollectionByStringProp } from '../utils/utils';
import { nreSearchParams } from './mock-search-params/nre';
import { telemonitoringSearchParams } from './mock-search-params/telemonitoring';
import { executionSiteFilterDefs, executionUserFilterDefs } from './search-page-filter-defs/execution-filter.defs';
import { paramsDateReviver } from './search-page-routines';
import { SearchFormType } from './search-page-type.enum';
import { SearchParam, SearchParams } from './search-param.model';

@Injectable({
    providedIn: 'root',
})
export class SearchPageService extends ServerRootService {
    private params: Dict<SearchParams> = {};

    // constructor
    constructor(
        private collectionService: EstensaCollectionService,
        private httpClient: HttpClient,
        private translate: TranslateService,
        private notificationService: NotificationService,
        private radioSettingsService: RadioSettingsService,
        private store: Store<RootState>,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
        // Injection for double surname feature
        private patientSettingsService: PatientSettingsService,
        //@TODO sistemare i tipi dei parametri di ricerca ed usare questo anzi che this.httpClient.post....
        private worklistService: WorklistService,
        private mpsService: MPSService,
        private visitRestService: VisitService,
        private ecgRestService: EcgService,
    ) {
        super();

        this.initService();
    }

    getResult(
        formType: SearchFormType,
        searchInfo:
            | IVisitWorklistSearchingParams
            | WorklistMpsSearchingParams
            | ExecutionWorklistSearchWorklist
            | WorklistSearchWorklist
            | ISpecialtyWorklistSearchParams,
    ): Observable<any> {
        const params = JSON.stringify(searchInfo);
        switch (formType) {
            case SearchFormType.ECG:
                return this.ecgRestService.getEcgWorklist(searchInfo as IVisitWorklistSearchingParams);
            //return this.httpClient.post(this.getServerRoot() + '/ECG/GetEcgWorklist', params, this.buildOptions());
            case SearchFormType.VISIT:
                return this.visitRestService.getVisitWorklist(searchInfo as IVisitWorklistSearchingParams);
            case SearchFormType.COMPLETE_CD:
                const completeParams: WorklistMpsSearchingParams = JSON.parse(params);
                return this.mpsService.retrieveCompletedListEx(completeParams);
            case SearchFormType.ON_PRINTING_CD:
                const printParams: WorklistMpsSearchingParams = JSON.parse(params);
                return this.mpsService.retrieveQueueFilteredEx(printParams);
            case SearchFormType.TELEMONITORING:
                return this.httpClient.post(
                    this.getServerRoot() + '/HealthPlatform/GetHealthPlatformWorklist',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.NRE:
                return this.httpClient.post(
                    this.getServerRoot() + '/NRE/GetWorklistForNRE',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.DUMMY_VNEXT2:
                return this.visitRestService.getVisitWorklist(searchInfo as IVisitWorklistSearchingParams);
            case SearchFormType.EXECUTION:
                return this.worklistService.getExecutionWorklist(
                    searchInfo as unknown as ExecutionWorklistSearchWorklist,
                );
            case SearchFormType.SPECIALTY:
                const specialtySearchParams: ISpecialtyWorklistSearchParams =
                    searchInfo as ISpecialtyWorklistSearchParams;
                const endPointRest = this.getSpecialtyEndPointBySearchType(specialtySearchParams.searchType);
                return this.httpClient.post(
                    this.getServerRoot() + `/Worklist/${endPointRest}`,
                    specialtySearchParams,
                    this.buildOptions(),
                );
            default:
                return of(null);
        }
    }

    getSpecialtyEndPointBySearchType = (searchType: number | null) => {
        switch (searchType) {
            case 0:
                return 'GetAllWorklist';
            case 1:
                return 'GetWorklistCPRadioReporting';
            case 2:
                return 'GetWorklistCPRadioReported';
            case 3:
                return 'GetWorklistCPRadioApproved';
            default:
                return 'GetAllWorklist';
        }
    };

    getSearchParams(form: SearchFormType): SearchParams {
        return this.params[form];
    }

    setSearchParams(form: SearchFormType, params: SearchParams) {
        this.params[form] = params;
    }

    async getSelectedItemIds(formType: SearchFormType): Promise<any> {
        return firstValueFrom(this.store.pipe(select(selectSeSearchSelectedItemIds(formType))));
    }

    async getSelectedItems(formType: SearchFormType, idPropertyName: string): Promise<any[]> {
        return firstValueFrom(this.getSelectedItems$(formType, idPropertyName));
    }

    getSelectedItems$(formType: SearchFormType, idPropertyName: string) {
        return this.store.pipe(select(selectSeSearchSelectedItems(formType, idPropertyName)));
    }

    async getItems(formType: SearchFormType) {
        return firstValueFrom(this.getItems$(formType));
    }

    getItems$(formType: SearchFormType) {
        return this.store.pipe(select(selectSeSearchItems(formType)));
    }

    getToolbarButtons() {
        return this.store.pipe(select(selectSeButtonsToolbarButtons));
    }

    getAssociatedVisits(idReport: string) {
        return this.visitRestService.getReportExaminations(idReport);
    }

    getWorklistCDA(formType: SearchFormType, searchInfo: SearchCda): Observable<any> {
        const params = JSON.stringify(searchInfo);
        switch (formType) {
            case SearchFormType.VISIT:
                return this.visitRestService.getVisitWorklist(searchInfo as IVisitWorklistSearchingParams);
            case SearchFormType.ECG:
                return this.ecgRestService.getEcgWorklist(searchInfo as IVisitWorklistSearchingParams);
            //return this.httpClient.post(this.getServerRoot() + '/ECG/GetEcgWorklist', params, this.buildOptions());
            case SearchFormType.SPECIALTY:
                const endPointRest = this.getSpecialtyEndPointBySearchType(searchInfo.searchType);
                return this.httpClient.post(
                    this.getServerRoot() + `/Worklist/${endPointRest}`,
                    params,
                    this.buildOptions(),
                );
            default:
                return of(null);
        }
    }

    // Carico le collection
    loadFormCollections(form: SearchFormType) {
        Object.entries(this.params[form].dataItemsSource).forEach(([name, itemsSource]) => {
            if (!!itemsSource.controllerName) {
                this.collectionService
                    .get(itemsSource.controllerName as any, itemsSource.methodName)
                    .pipe(
                        finalize(() =>
                            setTimeout(() => {
                                this.params[form].endDataLoading(name);
                            }),
                        ),
                    )
                    .subscribe({
                        next:
                            itemsSource.sortCollectionDataInfo && itemsSource.sortCollectionDataInfo.field
                                ? (data) =>
                                      this.params[form].addData(
                                          name,
                                          orderCollectionByStringProp(data, itemsSource.sortCollectionDataInfo.field),
                                      )
                                : (data) => this.params[form].addData(name, data),
                        error: (e) =>
                            this.notificationService.error(
                                e.message ? e.message : '',
                                this.translate.instant('ErrorLoadingData'),
                            ),
                    });
            } else {
                /*Collection locale*/
                this.getFromLocal(itemsSource.methodName)
                    .pipe(
                        finalize(() =>
                            setTimeout(() => {
                                this.params[form].endDataLoading(name);
                            }),
                        ),
                    )
                    .subscribe({
                        next: (data) => this.params[form].addData(name, data),
                        error: (e) =>
                            this.notificationService.error(
                                e.message ? e.message : '',
                                this.translate.instant('ErrorLoadingData'),
                            ),
                    });
            }
        });
    }

    /**
   IDLE = 1,
   PENDING = 2,
   CREATING = 3,
   DONE = 4,
   FAILED = 5,
   BLOCKED = 6,
   ABORTED = 7,
   WAITING = 8,
   BURNER = 9,
   UNKNOWN = 10
   */

    private getFromLocal(methodName: string) {
        switch (methodName) {
            // Liste caricate all'avvio.
            case 'GetMpsCdCompletedStatus':
                return of([
                    { id: 'ALL', description: this.translate.instant('All') },
                    { id: 'DONE', description: this.translate.instant('MPSJobsSuccedeed') },
                    { id: 'FAILED', description: this.translate.instant('MPSJobsFailed') },
                    { id: 'ABORTED', description: this.translate.instant('MPSJobsCancelled') },
                ]);

            case 'GetMpsOnPrintingCdStatus':
                return of([
                    { id: 'ALL', description: this.translate.instant('All') },
                    { id: 'IDLE', description: 'IDLE' },
                    { id: 'PENDING', description: 'PENDING' },
                    { id: 'CREATING', description: 'CREATING' },
                    { id: 'DONE', description: 'DONE' },
                    { id: 'FAILED', description: 'FAILED' },
                    { id: 'BLOCKED', description: 'BLOCKED' },
                    { id: 'ABORTED', description: 'ABORTED' },
                    { id: 'WAITING', description: 'WAITING' },
                    { id: 'BURNER', description: 'BURNER' },
                    { id: 'UNKNOWN', description: 'UNKNOWN' },
                ]);

            default:
                return of([]);
        }
    }

    private getFavoriteFilterValues(favorites: VNextGridUserPreference[]) {
        if (favorites) {
            const defaultFavorite = favorites.filter((favorite) => favorite.isDefault)[0];
            if (defaultFavorite) {
                return JSON.parse(defaultFavorite.searchParams);
            }
        }
        return {};
    }

    public calculateParamDate(delta: number) {
        const todayMidnight = new Date();
        todayMidnight.setHours(23, 59, 59, 0);
        const from = new Date();
        from.setDate(from.getDate() - (delta - 1));
        return { from, to: todayMidnight };
    }

    private loadFavoriteFilterValues(filterValues: any, deserializedSearchParams: any) {
        if (Object.keys(filterValues).length >= 1) {
            deserializedSearchParams.forEach((field: any) => {
                if (filterValues.hasOwnProperty(field.property.name)) {
                    field.property.value = filterValues[field.property.name];
                }
                if (field.property.name === 'date' && !!filterValues['dateDelta']) {
                    field.property.value = this.calculateParamDate(filterValues['dateDelta']);
                }
            });
        }
        return deserializedSearchParams;
    }

    private initService() {
        // Lettura dei settings
        const settings = this.radioSettingsService.radioSettings;
        const userSettings: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;

        // ECG - usare la variabile ecgSearchParams invece di settings.ecgWorklistSettings.searchParamDefs
        // per cablare il json prima di versarlo nei filtri
        let deserializedEcgSearchParams: SearchParam[] = JSON.parse(
            settings?.ecgWorklistSettings.searchParamDefs || '{}',
            this.reviver,
        );
        const ecgFavorites =
            userSettings?.preferences.searchGridUserPreference?.ecgWorklistSearchGrid?.collection ?? [];
        const ecgFilterValues = this.getFavoriteFilterValues(ecgFavorites || []);
        deserializedEcgSearchParams = this.loadFavoriteFilterValues(ecgFilterValues, deserializedEcgSearchParams);

        // VISIT - usare la variabile visitSearchParams invece di settings.visitWorklistSettings.searchParamDefs
        // per cablare il json prima di versarlo nei filtri
        let deserializedVisitSearchParams: SearchParam[] = JSON.parse(
            settings?.visitWorklistSettings.searchParamDefs || '{}',
            this.reviver,
        );
        const visitFavorites =
            userSettings?.preferences.searchGridUserPreference?.visitWorklistSearchGrid?.collection ?? [];
        const visitFilterValues = this.getFavoriteFilterValues(visitFavorites);
        deserializedVisitSearchParams = this.loadFavoriteFilterValues(visitFilterValues, deserializedVisitSearchParams);

        // CompleteCD
        const deserializedCompleteCdSearchParams: SearchParam[] = JSON.parse(
            settings?.completeCdWorklistSettings.searchParamDefs || '{}',
            this.reviver,
        );

        // OnPrinting
        const deserializedOnPrintingCdSearchParams: SearchParam[] = JSON.parse(
            settings?.onPrintingCdWorklistSettings.searchParamDefs || '{}',
            this.reviver,
        );

        // Telemonitoring
        const deserializedTelemonitoringSearchParams: SearchParam[] = JSON.parse(
            telemonitoringSearchParams,
            this.reviver,
        );

        const deserializedNreSearchParams: SearchParam[] = JSON.parse(nreSearchParams, this.reviver);

        //EXECUTION
        const deserializedExecutionSearchFilters: SearchParam[] = this.getExecutionFilterFromSettings();

        // DUMMY
        //const deserializedDummyVnext2SearchParams: SearchParam[] = JSON.parse(executionSearchFiltersDef, this.reviver);
        const deserializedDummyVnext2SearchParams: SearchParam[] = deserializedExecutionSearchFilters;

        // =============================================================================
        // SPECIALTY SEARCH PARAMS - Using server configuration
        // =============================================================================

      

        // Parse server configuration
        let deserializedSpecialtySearchParams: SearchParam[] = JSON.parse(
            settings?.specialtyWorklistSettings.searchParamDefs,
            this.reviver,
        );

        // Apply double surname layout transformation
        const isDoubleSurnameEnabled = this.isDoubleSurnameEnabled();
        deserializedSpecialtySearchParams = this.applyDoubleSurnameLayout(
            deserializedSpecialtySearchParams,
            isDoubleSurnameEnabled
        );

        const specialtyFavorites =
            userSettings?.preferences.searchGridUserPreference?.specialtyWorklistSearchGrid?.collection ?? [];
        const specialtyFilterValues = this.getFavoriteFilterValues(specialtyFavorites);
        deserializedSpecialtySearchParams = this.loadFavoriteFilterValues(
            specialtyFilterValues,
            deserializedSpecialtySearchParams,
        );

        /* EXECUTION CUSTOMIZATION (USER) */
        this.params[SearchFormType.EXECUTION] = new SearchParams(deserializedExecutionSearchFilters);

        /* SEARCH PARAMS */
        this.params[SearchFormType.ECG] = new SearchParams(deserializedEcgSearchParams);
        this.params[SearchFormType.VISIT] = new SearchParams(deserializedVisitSearchParams);
        this.params[SearchFormType.ON_PRINTING_CD] = new SearchParams(deserializedOnPrintingCdSearchParams);
        this.params[SearchFormType.COMPLETE_CD] = new SearchParams(deserializedCompleteCdSearchParams);
        this.params[SearchFormType.TELEMONITORING] = new SearchParams(deserializedTelemonitoringSearchParams);
        this.params[SearchFormType.NRE] = new SearchParams(deserializedNreSearchParams);
        this.params[SearchFormType.DUMMY_VNEXT2] = new SearchParams(deserializedDummyVnext2SearchParams);
        this.params[SearchFormType.SPECIALTY] = new SearchParams(deserializedSpecialtySearchParams);
    }

    /**
     * Questo metodo recupera la parte di setting per sito, quindi la parte di settings per utente e
     * costruisce il settings completo composto da i valori presenti nelle due parti in questione
     * @returns Settings completo
     */
    getExecutionFilterFromSettings(): SearchParam[] {
        const userSettings: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;
        try {
            // Parte di setting utente (specializzato se esiste)
            const executionUserSearchFilters: SearchParam[] = JSON.parse(
                userSettings?.preferences.adminPagesUserPreference?.execution?.filters ?? '[]',
                this.reviver,
            );
            return this.mergeExecutionFilterSettings(executionUserSearchFilters);
        } catch (err) {
            console.error(err);

            return [];
        }
    }

    /**
     * Questo metodo recupera la parte di settings per sito e la parte di settings utente
     * cablata nel codice (quella che possiamo chiamare valore di fabbrica). Quindi li unisce.
     * @returns Settings conpleto con valori di fabbrica
     */
    getDefaultSiteExecutionFilter(): SearchParam[] {
        // Valori di fabbrica della parte di setting specializzabile per utente
        const executionUserSearchFilters: SearchParam[] = executionUserFilterDefs();

        return this.mergeExecutionFilterSettings(executionUserSearchFilters);
    }

    cleanJsonString(jsonStr: string): string {
        return jsonStr
            .replace(/,\s*([}\]])/g, '$1') // virgole finali
            .replace(/([\{\[,])\s*([a-zA-Z0-9_]+)\s*:/g, '$1"$2":'); // doppie virgolette
    }

    /**
     * Questo metodo esegue l'unione tra la parte di setting NON specializzabile per utente
     * ovvero la parte di setting per sito e la parte di setting utente passata dall'esterno
     * @param executionUserSearchFilters parte di setting specializzabile per utente
     * @returns unione tra il settings per sito e quello passato dall'esterno
     */
    mergeExecutionFilterSettings(executionUserSearchFilters: SearchParam[], isOnError: boolean = false): SearchParam[] {
        let executionSiteSearchFilters: SearchParam[] = [];
        try {
            // Parte del setting NON specializzabile per utente
            const settings = this.radioSettingsService.radioSettings;
            const cleaned = this.cleanJsonString(settings!.executionWorklistSettings.searchParamDefs); // pulisco il JSON perchè arriva con una virgola in più da BE e genera errore in console.
            executionSiteSearchFilters = JSON.parse(cleaned, this.reviver); // QUESTI ARRIVANO DA RADIOSETTING
        } catch (err) {
            console.error(err);
            isOnError = true;
        }

        // Se è in stato di errore (passato dall'esterno o avvenuto qui), uso il default
        if (isOnError) {
            executionUserSearchFilters = executionUserFilterDefs(); // QUESTI ARRIVANO DA JSON A FE (execution-filter.defs.ts)
            executionSiteSearchFilters = executionSiteFilterDefs(); // QUESTI ARRIVANO DA JSON A FE (execution-filter.defs.ts)
        }

        let executionSearchFilters: SearchParam[] = [];

        for (let userSetting of executionUserSearchFilters) {
            let siteSetting = executionSiteSearchFilters.find(
                (_siteSetting) => _siteSetting.property.name === userSetting.property.name,
            );

            executionSearchFilters.push({
                ...userSetting,
                ...siteSetting,
                property: {
                    ...userSetting.property,
                    ...siteSetting?.property,
                },
            });
        }

        return executionSearchFilters;
    }

    private reviver(key: string, value: { from: string | Date | undefined; to: string | Date | undefined }) {
        if (key == 'value' && !!value) {
            return paramsDateReviver(value);
        } else if (key == 'defaultValue' && !!value) {
            // Va 'tradotto' anche il default value altrimenti esplode
            return paramsDateReviver(value);
        }

        return value;
    }

    // =============================================================================
    // DOUBLE SURNAME FEATURE - Helper methods
    // =============================================================================

    /**
     * Determines if double surname mode should be enabled.
     * Enabled when secondSurnameManagement=true AND enableRFC249Management=false
     */
    private isDoubleSurnameEnabled(): boolean {
        // TESTING: Remove hardcode and use actual settings
        const secondSurnameManagement = this.patientSettingsService.secondSurnameManagement() ?? false;
        const enableRFC249Management = this.patientSettingsService.enableRFC249() ?? false;

        // Disabled when RFC249 is enabled
        return secondSurnameManagement && !enableRFC249Management;
    }

    /**
     * Transforms form configuration for double surname mode.
     * - Activates firstSurname/secondSurname fields
     * - Hides surname field
     * - Reorganizes name field and shifts other fields down
     */
    private applyDoubleSurnameLayout(formConfig: SearchParam[], secondSurnameEnabled: boolean): SearchParam[] {
        if (!secondSurnameEnabled) {
            return formConfig;
        }

        return formConfig.map(field => {
            const fieldName = field.property.name;

            // Activate firstSurname and secondSurname fields
            if (fieldName === 'firstSurname' || fieldName === 'secondSurname') {
                return { ...field, active: true };
            }

            // Hide the single surname field
            if (fieldName === 'surname') {
                return { ...field, active: false };
            }

            // Move name field to row 1 and span full width
            if (fieldName === 'name') {
                return {
                    ...field,
                    layout: {
                        ...field.layout,
                        row: 1,
                        column: 0,
                        colspan: 2
                    }
                };
            }

            // Shift all other fields down by one row
            return {
                ...field,
                layout: {
                    ...field.layout,
                    row: (field.layout?.row ?? 0) + 1
                }
            };
        });
    }
}
